import { Divider } from '@mui/material';
import Grid from '@mui/material/Grid';
import CheckIcon from '@mui/icons-material/CheckCircle';
import CrossIcon from '@mui/icons-material/Cancel';
import { Dialog, TextV2 } from '@insights-ltd/design-library/components';
import { useTranslation } from 'react-i18next';
import { InviteeBulkResult, InviteeRequest, RequestError } from 'api';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';

type BulkUploadStatusDialogProps = {
  open: boolean;
  onClose: () => void;
  results: InviteeBulkResult[];
};

const StatusRow = ({
  invitee,
  error,
}: {
  invitee: InviteeRequest;
  error?: RequestError;
}) => {
  const { t } = useTranslation();

  return (
    <Grid container>
      <Grid
        container
        size={12}
        sx={{
          justifyContent: 'space-between',
        }}
      >
        <Grid>
          <TextV2 color="textSecondary" variant="bodyBold">
            {invitee.fullName}
          </TextV2>
        </Grid>
        <Grid>
          {error ? (
            <Grid container spacing={1}>
              <Grid>
                <CrossIcon
                  sx={(theme) => ({
                    color: theme.palette.error.main,
                  })}
                />
              </Grid>
              <Grid>
                <TextV2 color="error" variant="bodyBold">
                  {t(
                    'ui.event-management.events.bulk-upload.result-modal.not-added',
                  )}
                </TextV2>
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={1}>
              <Grid>
                <CheckIcon
                  sx={(theme) => ({
                    color: theme.palette.success.main,
                  })}
                />
              </Grid>
              <Grid>
                <TextV2 variant="bodyBold">
                  {t(
                    'ui.event-management.events.bulk-upload.result-modal.added',
                  )}
                </TextV2>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
      {error && (
        <Grid size={12}>
          <TextV2 color="error">
            {t(
              error.getRequestErrorKeys(
                'ui.event-management.generic.error',
                {},
                {
                  NO_PROFILE_AVAILABLE:
                    'ui.event-management.events.bulk-upload.result-modal.error.idtl-no-profile',
                  EMAIL_ADDRESS_ALREADY_TAKEN:
                    'ui.event-management.events.bulk-upload.result-modal.error.email-already-in-use',
                  INVALID_EMAIL_ADDRESS:
                    'ui.event-management.events.create.error.value-email-required',
                },
              ),
            )}
          </TextV2>
        </Grid>
      )}
      <Grid size={12}>
        <Divider
          sx={(theme) => ({
            marginTop: theme.spacing(spacingSizeMap.XS),
          })}
        />
      </Grid>
    </Grid>
  );
};

const BulkUploadStatusDialog = ({
  open,
  onClose,
  results,
}: BulkUploadStatusDialogProps) => {
  const { t } = useTranslation();
  const successCount = results.reduce<number>(
    (acc, bulkResult) => acc + (bulkResult.type === 'SUCCESS' ? 1 : 0),
    0,
  );
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('ui.event-management.events.bulk-upload.result-modal.title')}
      titleProps={{
        id: 'dialog-title',
      }}
      primaryButtonText={t(
        'ui.event-management.events.bulk-upload.result-modal.confirm',
      )}
      content={
        <Grid container spacing={2}>
          <Grid>
            <TextV2 color="textSecondary">
              {t(
                'ui.event-management.events.bulk-upload.result-modal.message',
                {
                  successCount,
                  totalCount: results.length,
                },
              )}
            </TextV2>
          </Grid>
          {results.map((bulkResult) => (
            <Grid key={bulkResult.inviteeData.email} size={12}>
              <StatusRow
                invitee={bulkResult.inviteeData}
                error={
                  bulkResult.type === 'FAILURE' ? bulkResult.error : undefined
                }
              />
            </Grid>
          ))}
        </Grid>
      }
    />
  );
};

export default BulkUploadStatusDialog;
